/**
 * UPT-2124 J4 (spec `web/SPEC-UPT-2124-J4.md` §2 n° 2-10) — les HUIT OUTILS d'interface : `ui_read`, `ui_navigate`, `ui_click`, `ui_fill`, `ui_select`,
 * `ui_scroll`, `ui_back`, `ui_undo`. Ce module FABRIQUE ceux qui lisent, naviguent, cliquent et défilent (J4a) et y joint ceux qui remplissent
 * (`uiWriteTools`, J4b) ; `useUiVoiceTools` les enregistre dans le registre des outils vocaux (J2a) au montage du chrome. Les outils métier déjà
 * enregistrés (`next_step`, `create_dossier`, outils de l'Idée) priment : la consigne du modèle les cite d'abord.
 *
 * Règles (spec) : chaque geste est MONTRÉ (fantôme 900 ms, annulable par bouton et `Échap`) puis joué par les événements natifs ; trois niveaux
 * (`uiRiskTable`) — libre, confirmé (carte J2d + « oui »), interdit (« Ce geste se fait à l'écran » + élément mis en évidence) ; ambiguïté = aucune
 * action ; introuvable = « Je ne vois pas … » + résumé de l'écran ; le journal (`postVoiceIntent`) garde l'outil, le niveau, le NOM de l'élément et la
 * page, jamais la valeur saisie. Un seul geste à la fois.
 */
import type { VoiceRegisteredTool, VoiceToolOutput } from '../voiceToolRegistry';
import {
  CLICKABLE_ROLES,
  collectCandidates,
  inDestructiveModal,
  isDisabled,
  matchStrength,
  resolveByName,
  resolveCandidate,
  scrollElementIntoView,
  scrollTo,
  submitsForm,
  type UiCandidate,
  type UiRole,
} from './uiActor';
import { clearUiGhost, playUiGhost, showUiHighlight } from './uiGhostStore';
import { readScreen } from './uiReader';
import { classifyUiGesture, riskOfName } from './uiRiskTable';
import { UI_TOOL_DEFINITIONS, UI_TOOL_NAMES, type UiToolName } from './uiToolDefs';
import { createUiToolKit, json, pageKey, type UiNavEntry, type UiToolsEnv, type UiWriteTools } from './uiToolKit';
import { createUiWriteTools, UI_UNDO_DEPTH } from './uiWriteTools';

export { UI_TOOL_DEFINITIONS, UI_TOOL_NAMES, pageKey };
export type { UiNavEntry, UiToolName, UiToolsEnv };
export { UI_UNDO_DEPTH };

const ROLE_ALIASES: Readonly<Record<string, UiRole>> = {
  button: 'button', bouton: 'button', knopf: 'button', pulsante: 'button',
  link: 'link', lien: 'link', verknupfung: 'link', collegamento: 'link',
  tab: 'tab', onglet: 'tab', registerkarte: 'tab', scheda: 'tab',
  menuitem: 'menuitem', row: 'row', ligne: 'row', zeile: 'row', riga: 'row',
  option: 'option', checkbox: 'checkbox', radio: 'radio', switch: 'switch', card: 'other',
};

/** Fabrique les outils pour un environnement (le chrome du portail ; les tests passent un document jsdom). */
export const createUiTools = (env: UiToolsEnv): VoiceRegisteredTool[] => {
  const kit = createUiToolKit(env);
  const { doc, t, journal, notFound, ambiguous, exclusive, cancelledOutput, performClick } = kit;
  const write: UiWriteTools | null = createUiWriteTools(kit);

  // ---------------------------------------------------------------------------------------------------------------- ui_read
  const uiRead = (args: Record<string, unknown>): VoiceToolOutput => {
    const region = typeof args.region === 'string' ? args.region : '';
    const result = readScreen(doc, region);
    if (result.ok === true) {
      journal('ui_read', 'applied', 'free', region || undefined);
      return json({ answer: result.text });
    }
    const options = result.candidates.join(result.error === 'region_ambiguous' ? ` ${t('voice.ui.or')} ` : ', ');
    return json({
      error: result.error,
      answer: t(result.error === 'region_ambiguous' ? 'voice.ui.answer.ambiguous' : 'voice.ui.answer.regionNotFound', { name: region, choices: options, options }),
    });
  };

  // ---------------------------------------------------------------------------------------------------------------- ui_navigate
  const uiNavigate = (args: Record<string, unknown>): Promise<VoiceToolOutput> =>
    exclusive(async () => {
      const destination = typeof args.destination === 'string' ? args.destination.trim() : '';
      if (!destination) {
        return json({ error: 'missing_destination', answer: t('voice.ui.answer.noDestination', { name: '' }) });
      }
      const seen = new Set<string>();
      const unique = env.navEntries().filter((entry) => (seen.has(entry.path) ? false : (seen.add(entry.path), true)));
      let best = 0;
      const scored = unique.map((entry) => {
        const strength = Math.max(matchStrength(destination, entry.label), matchStrength(destination, `${entry.trail} ${entry.label}`) > 0 ? 1 : 0);
        best = Math.max(best, strength);
        return { entry, strength };
      });
      if (best === 0) {
        journal('ui_navigate', 'invalid', 'free');
        const some = unique.slice(0, 8).map((entry) => entry.label).join(', ');
        return json({ error: 'not_found', answer: `${t('voice.ui.answer.noDestination', { name: destination })}${some ? ` ${some}` : ''}` });
      }
      const top = scored.filter((item) => item.strength === best).map((item) => item.entry);
      if (top.length > 1) {
        const choices = top
          .slice(0, 5)
          .map((entry) => (entry.trail && entry.trail !== entry.label ? `${entry.label} (${entry.trail})` : entry.label))
          .join(` ${t('voice.ui.or')} `);
        return json({
          error: 'ambiguous',
          answer: t('voice.ui.answer.ambiguous', { name: destination, choices }),
          candidates: top.slice(0, 5).map((entry) => ({ name: entry.label, context: entry.trail })),
        });
      }
      const target = top[0];
      const outcome = await playUiGhost({ label: t('voice.ui.ghost.navigate', { name: target.label }), ms: env.ghostMs });
      if (outcome === 'cancelled') return cancelledOutput('ui_navigate', 'free', target.label);
      // Le journal garde la page d'où part le geste : il est écrit avant que la navigation ne change le chemin.
      journal('ui_navigate', 'applied', 'free', target.label);
      env.navigate(target.path);
      return json({ done: true, answer: t('voice.ui.answer.navigated', { name: target.label }) });
    });

  // ---------------------------------------------------------------------------------------------------------------- ui_click
  const factsOf = (candidate: UiCandidate) => ({
    dataVoice: candidate.element.closest('[data-voice]')?.getAttribute('data-voice') ?? null,
    inDestructiveModal: inDestructiveModal(candidate.element),
    submits: submitsForm(candidate.element),
    touchedForm: write ? write.isTouched(candidate.element) : false,
    role: candidate.role,
  });

  const uiClick = (args: Record<string, unknown>): Promise<VoiceToolOutput> =>
    exclusive(async () => {
      const name = typeof args.name === 'string' ? args.name.trim() : '';
      if (!name) return notFound('');
      const roleArg = typeof args.role === 'string' ? ROLE_ALIASES[args.role.trim().toLowerCase()] : undefined;
      const roles: readonly UiRole[] = roleArg ? [roleArg] : CLICKABLE_ROLES;
      const resolution = resolveByName(doc, name, roles);
      if (resolution.kind === 'none') {
        // Un nom interdit qu'aucun bouton ne porte : « à l'écran » quand même (rien n'est mis en évidence).
        if (riskOfName(name) === 'forbidden') {
          journal('ui_click', 'rejected', 'forbidden', name);
          return json({ error: 'forbidden', forbidden: true, highlighted: false, answer: t('voice.ui.answer.forbidden') });
        }
        journal('ui_click', 'invalid', 'free', name);
        return notFound(name);
      }
      if (resolution.kind === 'ambiguous') return ambiguous(name, resolution.candidates);
      const candidate = resolution.candidate;
      const facts = factsOf(candidate);
      const decision = classifyUiGesture(candidate.name, facts);
      // Interdit à la voix : mis en évidence, rien n'est joué. Confirmé sans file de confirmation (J4b) : traité de même, plus sûr que jouer.
      if (decision.risk === 'forbidden' || (decision.risk === 'confirm' && !write)) {
        showUiHighlight({ label: t('voice.ui.ghost.forbidden'), element: candidate.element });
        journal('ui_click', 'rejected', 'forbidden', candidate.name);
        return json({ error: 'forbidden', forbidden: true, highlighted: true, answer: t('voice.ui.answer.forbidden') });
      }
      if (isDisabled(candidate.element)) {
        return json({ error: 'disabled', answer: t('voice.ui.answer.disabled', { name: candidate.name }) });
      }
      if (decision.risk === 'confirm' && write) {
        return write.askToConfirmClick(candidate, facts.touchedForm);
      }
      return performClick('ui_click', candidate, 'free');
    });

  // ---------------------------------------------------------------------------------------------------------------- ui_scroll / ui_back
  const uiScroll = (args: Record<string, unknown>): Promise<VoiceToolOutput> =>
    exclusive(async () => {
      const to = typeof args.to === 'string' ? args.to.trim() : '';
      const keyword = to.toLowerCase();
      if (keyword === 'top' || keyword === 'bottom' || keyword === 'up' || keyword === 'down') {
        const outcome = await playUiGhost({ label: t('voice.ui.ghost.scroll'), ms: env.ghostMs });
        if (outcome === 'cancelled') return cancelledOutput('ui_scroll', 'free', keyword);
        scrollTo(doc, keyword);
        journal('ui_scroll', 'applied', 'free', keyword);
        return json({ done: true, answer: t('voice.ui.answer.scrolled') });
      }
      if (!to) return notFound('');
      const resolution = resolveCandidate(collectCandidates(doc).filter((candidate) => candidate.role !== 'textbox'), to);
      if (resolution.kind === 'none') {
        journal('ui_scroll', 'invalid', 'free', to);
        return notFound(to);
      }
      if (resolution.kind === 'ambiguous') return ambiguous(to, resolution.candidates);
      const target = resolution.candidate;
      const outcome = await playUiGhost({ label: t('voice.ui.ghost.scrollTo', { name: target.name }), element: target.element, ms: env.ghostMs });
      if (outcome === 'cancelled') return cancelledOutput('ui_scroll', 'free', target.name);
      scrollElementIntoView(target.element);
      journal('ui_scroll', 'applied', 'free', target.name);
      return json({ done: true, answer: t('voice.ui.answer.scrolled') });
    });

  const uiBack = (): Promise<VoiceToolOutput> =>
    exclusive(async () => {
      const outcome = await playUiGhost({ label: t('voice.ui.ghost.back'), ms: env.ghostMs });
      if (outcome === 'cancelled') return cancelledOutput('ui_back', 'free', '');
      journal('ui_back', 'applied', 'free');
      env.navigate(-1);
      return json({ done: true, answer: t('voice.ui.answer.back') });
    });

  const tool = (name: UiToolName, execute: VoiceRegisteredTool['execute']): VoiceRegisteredTool => ({ definition: UI_TOOL_DEFINITIONS[name], execute });
  return [
    tool('ui_read', (args) => uiRead(args)),
    tool('ui_navigate', (args) => uiNavigate(args)),
    tool('ui_click', (args) => uiClick(args)),
    tool('ui_scroll', (args) => uiScroll(args)),
    tool('ui_back', () => uiBack()),
    ...(write ? write.tools : []),
  ];
};

/** Arrête tout fantôme en cours (démontage du chrome). */
export const stopUiGestures = (): void => clearUiGhost('cancelled');
